import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import React, { createContext, useCallback, useContext, useState } from 'react';

import { addHours, format } from 'date-fns';
import IOrder from '~/interfaces/IOrder';
import IReport from '~/interfaces/IReport';
import { DB, Realtime } from '~/services/firebase';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';

import ITableOrders from '~/interfaces/ITableOrders';
import { bff } from '~/services/bff';
import { handlePrint } from '~/utils/handlePrint';
import { useAuth } from './auth';
import { clearFormatPhoneNumber } from '~/utils/getPhoneFormatted';
import axios from 'axios';
import { isWithinFiveMinutes } from '~/utils/isWithinFiveMinutes';

interface LoadOrderWithRangeDateProps {
  id: string;
  initialDate: string;
  initialHour: string;
  finalDate: string;
  finalHour: string;
  isNew?: boolean;
}

interface OrdersContextData {
  orders: IOrder[] | null;
  loadOrders(id: string, isNew?: boolean): Promise<void>;
  loadOrdersWithRangeDate(data: LoadOrderWithRangeDateProps): Promise<void>;
  loadMore(id: string): Promise<void>;
  approve(id: string): Promise<void>;
  cancel(id: string): Promise<void>;
  delivery(id: string): Promise<void>;
  finished(id: string): Promise<void>;
  newOrder(id: string): Promise<void>;
  openPos: boolean;
  setOpenPos(value: boolean): void;
  print: boolean;
  updateOrder(uid: string, data: IOrder): Promise<void>;
  setPrint(value: boolean): void;
  updateDeliverymen(uid: string, deliverymen: string): void;
  updatePaymentMethod(uid: string, paymentMethod: string): void;
  updateOrderList(uid: string, data: IOrder): void;
  orderTables: ITableOrders[];
  loadOrdersTable: () => Promise<void>;
  removeOrder: (id: string) => void;
  initialDate: string;
  setInitialDate: (value: string) => void;
  initialHour: string;
  setInitialHour: (value: string) => void;
  finalDate: string;
  setFinalDate: (value: string) => void;
  finalHour: string;
  setFinalHour: (value: string) => void;
}

const { REACT_APP_FIREBASE_PROJECT, REACT_APP_CHATBOT_WPP, REACT_APP_CHATBOT_WPP_SEND_MESSAGE_ONLY } = process.env;
const OrdersContext = createContext<OrdersContextData>({} as OrdersContextData);

export const OrdersProvider: React.FC = ({ children }) => {
  const { company } = useAuth();

  const [orders, setOrders] = useState<IOrder[] | null>(null);
  const [last, setLast] = useState({});
  const [perPage] = useState(50);
  const [yearMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [day] = useState(format(new Date(), 'dd'));
  const [openPos, setOpenPos] = useState(false);
  const [print, setPrint] = useState(false);
  const [orderTables, setOrderTables] = useState<ITableOrders[]>([]);
  const [page, setPage] = useState(2);

  // filter
  const [initialDate, setInitialDate] = useState(
    format(new Date(), 'yyyy-MM-dd'),
  );
  const [initialHour, setInitialHour] = useState('00:00');
  const [finalDate, setFinalDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [finalHour, setFinalHour] = useState('23:59');

  const removeOrder = useCallback(
    (id: string) => {
      const filterOrders = (orders || []).filter(item => item.uid !== id);
      setOrders(filterOrders);
    },
    [orders],
  );

  const loadOrdersTable = useCallback(async () => {
    try {
      const response = await bff.get(
        `/tables-orders/${REACT_APP_FIREBASE_PROJECT}`,
      );

      const parseTableOrders = response.data.map((item: any) => ({
        ...item,
        total_formmated: getCurrencyFormatted(parseFloat(item.total)),
        alert: isWithinFiveMinutes(new Date(), new Date(item.updated_at)),
        created_at_formatted: format(
          new Date(item.created_at),
          'dd/MM/yyyy HH:mm',
        ),
        updated_at_formatted: format(
          new Date(item.updated_at),
          'dd/MM/yyyy HH:mm',
        ),
      }));

      setOrderTables(parseTableOrders);
    } catch (err) {
      console.log(err);
    }
  }, []);

  const updateStatusRealtime = useCallback(
    async (user_id: string, order_id: string, status: string) => {
      const item = {
        order_id,
        status,
      };

      const ref = Realtime.ref(`orders/${user_id}`);

      const newNotificationRef = ref.push();

      await newNotificationRef.set(item);
    },
    [],
  );

  const loadOrdersWithRangeDate = useCallback(
    async ({
      id,
      initialDate,
      initialHour,
      finalDate,
      finalHour,
      isNew = false,
    }: LoadOrderWithRangeDateProps) => {
      const [iHour, iMinute] = initialHour.split(':');
      const [eHour, eMinute] = finalHour.split(':');

      const initDate = new Date(initialDate);
      initDate.setHours(parseInt(iHour));
      initDate.setMinutes(parseInt(iMinute));
      initDate.setSeconds(0);
      initDate.setDate(initDate.getDate() + 1);

      const finishDate = new Date(finalDate);
      finishDate.setHours(parseInt(eHour));
      finishDate.setMinutes(parseInt(eMinute));
      finishDate.setSeconds(59);
      finishDate.setDate(finishDate.getDate() + 1);

      const q = query(
        collection(DB, 'orders'),
        where('company_user_id', '==', id),
        where('created_at', '>=', initDate),
        where('created_at', '<=', finishDate),
        orderBy('created_at', 'desc'),
      );

      const querySnapshot = await getDocs(q);

      const lastItem = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLast(lastItem);

      const items: IOrder[] = [];

      querySnapshot.docs.forEach((item: any) => {
        const data = item.data() as IOrder;
        const created_at = new Date(data.created_at.seconds * 1000);

        const parseItem: IOrder = {
          ...data,
          uid: item.id,
          shippingCost_formatted: getCurrencyFormatted(data.shippingCost),
          subtotal_formatted: getCurrencyFormatted(data.subtotal),
          total_formatted: getCurrencyFormatted(data.total),
          created_at_formatted: format(created_at, 'dd/MM/yyyy HH:mm'),
        };

        items.push(parseItem);
      });

      console.log(items);

      setOrders(items);

      if (
        (isNew && company.auto_print === '2' && items[0].order_type === '1') ||
        (isNew && company.auto_print === '1')
      ) {
        handlePrint({ order: items[0], company });
      }
    },
    [company],
  );

  const loadOrders = useCallback(
    async (id: string, isNew = false) => {
      const response = await bff.get(`/orders/${REACT_APP_FIREBASE_PROJECT}`);
      const items = response.data;
      setOrders(items);

      if (
        (isNew && company.auto_print === '2' && items[0].order_type === '1') ||
        (isNew && company.auto_print === '1')
      ) {
        if (items[0].table?.uid) {
          console.log('pedido de cozinha imprime parcial');
          handlePrint({ order: items[0], company, parcial: true });
        } else {
          handlePrint({ order: items[0], company });
        }
      }

      await bff.delete(`/orders/${REACT_APP_FIREBASE_PROJECT}`);
    },
    [company],
  );

  const loadMore = useCallback(
    async (id: string) => {
      setPage(value => value + 1);

      const response = await bff.get(`/orders/${REACT_APP_FIREBASE_PROJECT}`, {
        params: { page },
      });

      setOrders(response.data);
    },
    [page],
  );

  const finished = useCallback(
    async (uid: string) => {
      const items = [...(orders || [])];

      const findIndex = items.findIndex(item => item.uid === uid);

      if (findIndex !== -1) {
        const orderUpdated: IOrder = {
          ...items[findIndex],
          status: '3',
        };

        if (orderUpdated.uid) {
          await updateDoc(doc(DB, 'orders', orderUpdated.uid), {
            status: orderUpdated.status,
          });

          await bff.delete(`orders/${REACT_APP_FIREBASE_PROJECT}`);

          await updateStatusRealtime(
            orderUpdated.client_id,
            orderUpdated.uid,
            orderUpdated.status,
          );

          items[findIndex] = orderUpdated;

          setOrders(items);

          const reportsRef = collection(DB, 'reports');
          const q = query(
            reportsRef,
            where('company_id', '==', orderUpdated.company_id),
          );
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const item = {
              ...querySnapshot.docs[0].data(),
              uid: querySnapshot.docs[0].id,
            } as IReport;

            let orders_quantity = 0;
            let sales_total = orderUpdated.total;

            if (item.data[yearMonth] && item.data[yearMonth][day]) {
              orders_quantity += item.data[yearMonth][day].orders_quantity;
              sales_total += item.data[yearMonth][day].sales_total;
            }

            await updateDoc(doc(DB, 'reports', item.uid), {
              [`data.${yearMonth}.${day}`]: {
                orders_quantity,
                sales_total,
              },
            });
          }
        }
      }
    },
    [orders, yearMonth, day, updateStatusRealtime],
  );

  const updateDeliverymen = useCallback(
    async (uid: string, deliverymen: string) => {
      const items = [...(orders || [])];

      const findIndex = items.findIndex(item => item.uid === uid);

      if (findIndex !== -1) {
        const orderUpdated: IOrder = {
          ...items[findIndex],
          deliverymen,
        };

        if (orderUpdated.uid) {
          await updateDoc(doc(DB, 'orders', orderUpdated.uid), {
            deliverymen,
          });

          await bff.delete(`orders/${REACT_APP_FIREBASE_PROJECT}`);

          items[findIndex] = orderUpdated;

          setOrders(items);
        }
      }
    },
    [orders],
  );

  const newOrder = useCallback(
    async (uid: string) => {
      const items = [...(orders || [])];

      const findIndex = items.findIndex(item => item.uid === uid);

      if (findIndex !== -1) {
        const orderUpdated: IOrder = {
          ...items[findIndex],
          status: '0',
        };

        if (orderUpdated.uid) {
          await updateDoc(doc(DB, 'orders', orderUpdated.uid), {
            status: orderUpdated.status,
          });

          await bff.delete(`orders/${REACT_APP_FIREBASE_PROJECT}`);

          await updateStatusRealtime(
            orderUpdated.client_id,
            orderUpdated.uid,
            orderUpdated.status,
          );

          items[findIndex] = orderUpdated;

          setOrders(items);
        }
      }
    },
    [orders, updateStatusRealtime],
  );

  const sendWhatsAppStatusMessage = useCallback(
    async (number: string, message: string) => {
      try {
        // Remove caracteres não numéricos (como parênteses, espaços, traços)
        const sanitizedNumber = number.replace(/[^\d]/g, '');

        // Ajusta o formato para números válidos no Brasil
        // const parseNumber =
        //   sanitizedNumber.length >= 13 &&
        //   sanitizedNumber.startsWith('55') &&
        //   sanitizedNumber[4] === '9'
        //     ? sanitizedNumber.slice(0, 4) + sanitizedNumber.slice(5)
        //     : sanitizedNumber;

        // await axios.post(
        //   `${REACT_APP_CHATBOT_WPP}/session/${REACT_APP_FIREBASE_PROJECT}/send-message`,
        //   { number: parseNumber, message },
        // );

        await axios.post(
          `${REACT_APP_CHATBOT_WPP}/session/${REACT_APP_FIREBASE_PROJECT}/send-message`,
          { number: sanitizedNumber, message },
        );
      } catch (err) {
        console.log('Erro ao enviar mensagem: ', err);
      }
    },
    [],
  );

  const sendWhatsAppN8n = useCallback(
    async (number: string, message: string) => {
      try {
        // Remove caracteres não numéricos (como parênteses, espaços, traços)
        const sanitizedNumber = number.replace(/[^\d]/g, '');

        // Formata o número para o padrão esperado pelo n8n (5582998382776@s.whatsapp.net)
        const formattedNumber = `${sanitizedNumber}@s.whatsapp.net`;

        // Envia a mensagem através do webhook do n8n
        await axios.post(
          'https://fatiaprime-n8n.syzuq1.easypanel.host/webhook/e6c451db-5cbf-4fe4-84c7-7d8214ad447a',
          {
            instance: REACT_APP_FIREBASE_PROJECT,
            whatsapp: formattedNumber,
            message: message,
          },
        );
      } catch (err) {
        console.log('Erro ao enviar mensagem via n8n: ', err);
      }
    },
    [],
  );

  const approve = useCallback(
    async (uid: string) => {
      const items = [...(orders || [])];

      const findIndex = items.findIndex(item => item.uid === uid);

      if (findIndex !== -1) {
        const orderUpdated: IOrder = {
          ...items[findIndex],
          status: '1',
        };

        if (orderUpdated.uid) {
          await updateDoc(doc(DB, 'orders', orderUpdated.uid), {
            status: orderUpdated.status,
          });

          await bff.delete(`orders/${REACT_APP_FIREBASE_PROJECT}`);

          await updateStatusRealtime(
            orderUpdated.client_id,
            orderUpdated.uid,
            orderUpdated.status,
          );

          items[findIndex] = orderUpdated;

          setOrders(items);

          if (company.whatsapp_order === '1') {
            const message = `Olá, ${orderUpdated.name}! O pedido N°${orderUpdated.code} foi aceito e já está sendo preparado.`;

            await sendWhatsAppN8n(
              `55${clearFormatPhoneNumber(orderUpdated.whatsapp)}`,
              message,
            );
          }
        }
      }
    },
    [orders, updateStatusRealtime, company, sendWhatsAppStatusMessage],
  );

  const cancel = useCallback(
    async (uid: string) => {
      const items = [...(orders || [])];

      const findIndex = items.findIndex(item => item.uid === uid);

      if (findIndex !== -1) {
        const orderUpdated: IOrder = {
          ...items[findIndex],
          status: '4',
        };

        if (orderUpdated.uid) {
          await updateDoc(doc(DB, 'orders', orderUpdated.uid), {
            status: orderUpdated.status,
          });

          await bff.delete(`orders/${REACT_APP_FIREBASE_PROJECT}`);

          await updateStatusRealtime(
            orderUpdated.client_id,
            orderUpdated.uid,
            orderUpdated.status,
          );

          items[findIndex] = orderUpdated;

          setOrders(items);

          if (company.whatsapp_order === '1') {
            const message = `Olá, ${orderUpdated.name}. O pedido N°${orderUpdated.code} foi cancelado. Infelizmente não vamos consegu atendê-lo(a).`;

            await sendWhatsAppN8n(
              `55${clearFormatPhoneNumber(orderUpdated.whatsapp)}`,
              message,
            );
          }
        }
      }
    },
    [orders, updateStatusRealtime, company, sendWhatsAppStatusMessage],
  );

  const delivery = useCallback(
    async (uid: string) => {
      const items = [...(orders || [])];

      const findIndex = items.findIndex(item => item.uid === uid);

      if (findIndex !== -1) {
        const orderUpdated: IOrder = {
          ...items[findIndex],
          status: '2',
        };

        if (orderUpdated.uid) {
          await updateDoc(doc(DB, 'orders', orderUpdated.uid), {
            status: orderUpdated.status,
          });

          await bff.delete(`orders/${REACT_APP_FIREBASE_PROJECT}`);

          await updateStatusRealtime(
            orderUpdated.client_id,
            orderUpdated.uid,
            orderUpdated.status,
          );

          items[findIndex] = orderUpdated;

          setOrders(items);

          if (company.whatsapp_order === '1') {
            const message = `Olá, ${orderUpdated.name}. O pedido N°${orderUpdated.code} saiu para entrega.`;

            await sendWhatsAppN8n(
              `55${clearFormatPhoneNumber(orderUpdated.whatsapp)}`,
              message,
            );
          }
        }
      }
    },
    [orders, updateStatusRealtime, company, sendWhatsAppStatusMessage],
  );

  const updatePaymentMethod = useCallback(
    async (uid: string, paymentMethod: string) => {
      const items = [...(orders || [])];

      const findIndex = items.findIndex(item => item.uid === uid);

      if (findIndex !== -1) {
        const orderUpdated: IOrder = {
          ...items[findIndex],
          paymentMethod,
        };

        if (orderUpdated.uid) {
          await updateDoc(doc(DB, 'orders', orderUpdated.uid), {
            paymentMethod: orderUpdated.paymentMethod,
          });

          await bff.delete(`orders/${REACT_APP_FIREBASE_PROJECT}`);

          items[findIndex] = orderUpdated;
          setOrders(items);
        }
      }
    },
    [orders],
  );

  const updateOrderList = useCallback(
    (uid: string, data: IOrder) => {
      const items = [...(orders || [])];

      const findIndex = items.findIndex(item => item.uid === uid);

      if (findIndex !== -1) {
        items[findIndex] = data;
        setOrders(items);
      }
    },
    [orders],
  );

  const updateOrder = useCallback(
    async (uid: string, data: IOrder) => {
      const items = [...(orders || [])];

      const findIndex = items.findIndex(item => item.uid === uid);

      await updateDoc(doc(DB, 'orders', uid), {
        shippingCost: data.shippingCost,
        subtotal: data.subtotal,
        total: data.subtotal + data.shippingCost,
      });

      await bff.delete(`orders/${REACT_APP_FIREBASE_PROJECT}`);

      if (findIndex !== -1) {
        items[findIndex] = data;
        setOrders(items);
      }
    },
    [orders],
  );

  return (
    <OrdersContext.Provider
      value={{
        orders,
        updateOrder,
        loadOrders,
        loadOrdersWithRangeDate,
        loadMore,
        approve,
        cancel,
        delivery,
        finished,
        newOrder,
        openPos,
        setOpenPos,
        print,
        setPrint,
        updateDeliverymen,
        updatePaymentMethod,
        orderTables,
        loadOrdersTable,
        removeOrder,
        initialDate,
        setInitialDate,
        initialHour,
        setInitialHour,
        finalDate,
        setFinalDate,
        finalHour,
        setFinalHour,
        updateOrderList,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
};

export function useOrders(): OrdersContextData {
  const context = useContext(OrdersContext);
  return context;
}
